import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClickAwayListener, Popper } from '@mui/material';
import useSearchListKeyNav from '@/components/Approval/@hooks/useSearchListKeyNav';
import useTabOutClose from '@/components/Approval/@hooks/useTabOutClose';
import Header from './Header';
import Combobox from './Combobox';
import DropdownPanel from './DropdownPanel';
import Footer from './Footer';
import { isKeywordSearchable } from './constants';
import { Referrer, ReferrerFieldProps } from './types';
import { Root } from './styles';

/**
 * 참조인(CC) 영역 원자 컴포넌트 세트의 루트.
 * 완전 controlled — Jotai atom 참조 금지(atom 배선은 호스트 페이지 책임). 검색 훅은 useUserSearch prop 으로 DI.
 * TODO(후속 PR): atom 배선, useUserSearch 실제 구현(usePassportSearch 어댑터) 교체, 역할별 재사용 시 icon/label 등 교체.
 */
const ReferrerField = ({
  selected,
  onChange,
  keyword,
  onKeywordChange,
  open: openProp,
  defaultOpen,
  onOpenChange,
  useUserSearch,
  maxCount,
  allowSelectAll = true,
  disabled,
  label,
  icon,
  placeholder,
  emptyMessage,
  hideHeader = false,
  hideFooter = false,
  showReset = true,
  onReset,
  isDeactivated,
  isLocked,
}: ReferrerFieldProps) => {
  const { t } = useTranslation();
  // 기본 문구는 t() 가 필요해 파라미터 기본값으로 둘 수 없음 — 훅 이후에 해석
  const resolvedLabel = label ?? t('참조인');
  const resolvedPlaceholder = placeholder ?? t('소속 또는 이름 검색');
  const resolvedEmptyMessage = emptyMessage ?? t('등록된 참조인이 없어요.');

  const isControlledOpen = typeof openProp === 'boolean';
  const [uncontrolledOpen, setUncontrolledOpen] = useState<boolean>(
    defaultOpen ?? false,
  );
  const open = isControlledOpen ? openProp : uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlledOpen) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlledOpen, onOpenChange],
  );

  const anchorRef = useRef<HTMLDivElement | null>(null);

  const { data, isLoading } = useUserSearch(keyword);

  // 참조 추천 정책이 없어 키워드 미달 구간엔 보여줄 게 없음. open 은 유지 — 글자를 채우면 그대로 열림
  const isPanelOpen = open && !disabled && isKeywordSearchable(keyword);

  const selectedIds = useMemo(
    () => new Set(selected.map((s) => s.id)),
    [selected],
  );

  // 잠긴(양식 고정) 항목은 어느 경로로도 빠지지 않는다 — 칩 X, 드롭다운 체크 해제,
  // 전체선택 해제, 초기화, 백스페이스 전부 여기서 한 번에 걸러진다.
  const lockedIds = useMemo(
    () => new Set(selected.filter((s) => isLocked?.(s)).map((s) => s.id)),
    [selected, isLocked],
  );
  const removableCount = selected.length - lockedIds.size;

  const handleToggle = useCallback(
    (r: Referrer) => {
      if (selectedIds.has(r.id)) {
        if (lockedIds.has(r.id)) return;
        onChange(selected.filter((s) => s.id !== r.id));
      } else {
        if (typeof maxCount === 'number' && selected.length >= maxCount) return;
        onChange([...selected, r]);
      }
    },
    [selected, selectedIds, lockedIds, onChange, maxCount],
  );

  const handleRemove = useCallback(
    (id: string) => {
      if (lockedIds.has(id)) return;
      onChange(selected.filter((s) => s.id !== id));
    },
    [selected, lockedIds, onChange],
  );

  const handleToggleAll = useCallback(() => {
    const visibleIds = data.map((d) => d.id);
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
    if (allSelected) {
      // 현재 화면에 보이는 옵션만 해제. 잠긴 항목은 그대로 둔다.
      onChange(
        selected.filter(
          (s) => !visibleIds.includes(s.id) || lockedIds.has(s.id),
        ),
      );
    } else {
      // maxCount 이내에서만 추가.
      const merged = [...selected];
      const existing = new Set(selected.map((s) => s.id));
      for (const d of data) {
        if (existing.has(d.id)) continue;
        if (typeof maxCount === 'number' && merged.length >= maxCount) break;
        merged.push(d);
      }
      onChange(merged);
    }
  }, [data, selected, selectedIds, lockedIds, onChange, maxCount]);

  const handleReset = useCallback(() => {
    if (onReset) onReset();
    // 초기화는 사용자가 직접 추가한 항목만 지운다 — 잠긴 항목은 남는다.
    else onChange(selected.filter((s) => lockedIds.has(s.id)));
  }, [onReset, onChange, selected, lockedIds]);

  const handleInputFocus = useCallback(() => {
    if (!disabled) setOpen(true);
  }, [disabled, setOpen]);

  // focusedIndex: 전체선택 행 있으면 0=전체선택·1..N=options[i-1], 없으면 0..N-1, 빈 리스트=-1
  const hasSelectAllRow = allowSelectAll && data.length > 0;
  const itemsLen = data.length + (hasSelectAllRow ? 1 : 0);

  const handleSelectIndex = useCallback(
    (index: number) => {
      if (hasSelectAllRow && index === 0) {
        handleToggleAll();
      } else {
        const target = data[hasSelectAllRow ? index - 1 : index];
        if (target) handleToggle(target);
      }
    },
    [hasSelectAllRow, data, handleToggle, handleToggleAll],
  );

  const { focusedIndex, handleKeyDown } = useSearchListKeyNav({
    open: isPanelOpen,
    setOpen,
    itemCount: itemsLen,
    items: data,
    onSelectIndex: handleSelectIndex,
    busy: isLoading,
  });

  const handleClickAway = useCallback(() => {
    if (open) setOpen(false);
  }, [open, setOpen]);

  // Tab 이동/끝단 이탈은 handleKeyDown 담당 — 이 blur 는 그 외 Tab-out 의 안전장치
  const handleRootBlur = useTabOutClose<HTMLDivElement>(open, () =>
    setOpen(false),
  );

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Root onBlur={handleRootBlur}>
        {!hideHeader && <Header label={resolvedLabel} icon={icon} />}
        <Combobox
          ref={anchorRef}
          selected={selected}
          isDeactivated={isDeactivated}
          lockedIds={lockedIds}
          onRemove={handleRemove}
          keyword={keyword}
          onKeywordChange={(v) => {
            onKeywordChange(v);
            if (!open) setOpen(true);
          }}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={resolvedPlaceholder}
          disabled={disabled}
          open={isPanelOpen}
        />
        {!hideFooter && (
          <Footer
            count={selected.length}
            emptyMessage={resolvedEmptyMessage}
            showReset={showReset && removableCount > 0}
            onReset={handleReset}
            disabled={disabled}
          />
        )}
        <Popper
          open={isPanelOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{
            width: anchorRef.current?.offsetWidth ?? 'auto',
            zIndex: 1300,
          }}
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        >
          <DropdownPanel
            options={data}
            selected={selected}
            lockedIds={lockedIds}
            keyword={keyword}
            isLoading={isLoading}
            focusedIndex={focusedIndex}
            onToggle={handleToggle}
            allowSelectAll={allowSelectAll}
            onToggleAll={handleToggleAll}
            maxCount={maxCount}
          />
        </Popper>
      </Root>
    </ClickAwayListener>
  );
};

export default ReferrerField;
export type { Referrer, ReferrerFieldProps, UseUserSearch } from './types';
